import React, { useState, useEffect } from "react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { useContacts } from "src/features/contacts/hooks/useContacts";
import { ImageUploader } from "src/components/ImageUploader";

export function PropertyListingForm({
  listing,
  onSubmit,
  onCancel,
  isPending,
  isRental = false,
}) {
  const [formData, setFormData] = useState({
    title: "",
    contact_id: "",
    listing_type: isRental ? "rental" : "sale",
    status: "draft",
    property_type: "",
    city: "",
    locality: "",
    bedrooms: "",
    bathrooms: "",
    area_sqft: "",
    asking_price: "",
    rent_amount: "",
    security_deposit: "",
    maintenance_charges: "",
    configuration: "",
  });

  const { data: contactsData, fetch: fetchContacts } = useContacts();
  const contacts = contactsData.contacts || [];

  useEffect(() => {
    // Load contacts so the listing can be linked to its owner/seller.
    fetchContacts({ per_page: 100 });
  }, [fetchContacts]);

  useEffect(() => {
    if (listing) {
      setFormData({
        title: listing.title || "",
        contact_id: listing.contact?.id ? String(listing.contact.id) : "",
        listing_type: listing.listing_type || (isRental ? "rental" : "sale"),
        status: listing.status || "draft",
        property_type: listing.property_type || "",
        city: listing.city || "",
        locality: listing.locality || "",
        bedrooms: listing.bedrooms || "",
        bathrooms: listing.bathrooms || "",
        area_sqft: listing.area_sqft || "",
        asking_price: listing.asking_price || "",
        rent_amount: listing.rent_amount || "",
        security_deposit: listing.security_deposit || "",
        maintenance_charges: listing.maintenance_charges || "",
        configuration: listing.configuration || "",
      });
    }
  }, [listing, isRental]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [contactError, setContactError] = useState("");

  // Photo gallery changes, merged into the submit payload under __images.
  const [photos, setPhotos] = useState({ newFiles: [], removedIds: [] });
  useEffect(() => {
    setPhotos({ newFiles: [], removedIds: [] });
  }, [listing]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.contact_id) {
      setContactError("Please select a contact for this listing.");
      return;
    }
    setContactError("");

    onSubmit({ ...formData, __images: photos });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-full overflow-hidden"
    >
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              name="title"
              required
              placeholder="e.g. Beautiful 3BHK in Koramangala"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          <div>
            <Label htmlFor="contact_id">Contact *</Label>
            <Select
              value={formData.contact_id}
              onValueChange={(val) => {
                handleSelectChange("contact_id", val);
                setContactError("");
              }}
            >
              <SelectTrigger id="contact_id">
                <SelectValue placeholder="Select a contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-slate-400">
                    No contacts available
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <SelectItem key={contact.id} value={String(contact.id)}>
                      {contact.name}
                      {contact.phone ? ` · ${contact.phone}` : ""}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {contactError && (
              <p className="mt-1 text-sm text-red-600">{contactError}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(val) => handleSelectChange("status", val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="under_offer">Under Offer</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="rented">Rented</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Property Type</Label>
              <Select
                value={formData.property_type}
                onValueChange={(val) => handleSelectChange("property_type", val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="house">House/Villa</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="plot">Plot</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                name="city"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="locality">Locality</Label>
              <Input
                id="locality"
                name="locality"
                placeholder="e.g. Whitefield"
                value={formData.locality}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bedrooms">Bedrooms</Label>
              <Input
                id="bedrooms"
                name="bedrooms"
                type="number"
                value={formData.bedrooms}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="bathrooms">Bathrooms</Label>
              <Input
                id="bathrooms"
                name="bathrooms"
                type="number"
                value={formData.bathrooms}
                onChange={handleChange}
              />
            </div>
            <div>
              <Label htmlFor="area_sqft">Area (Sqft)</Label>
              <Input
                id="area_sqft"
                name="area_sqft"
                type="number"
                value={formData.area_sqft}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {isRental ? (
              <>
                <div>
                  <Label htmlFor="rent_amount">Rent Amount (₹)</Label>
                  <Input
                    id="rent_amount"
                    name="rent_amount"
                    type="number"
                    value={formData.rent_amount}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label htmlFor="security_deposit">Security Deposit (₹)</Label>
                  <Input
                    id="security_deposit"
                    name="security_deposit"
                    type="number"
                    value={formData.security_deposit}
                    onChange={handleChange}
                  />
                </div>
              </>
            ) : (
              <div>
                <Label htmlFor="asking_price">Asking Price (₹)</Label>
                <Input
                  id="asking_price"
                  name="asking_price"
                  type="number"
                  value={formData.asking_price}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Photos</Label>
          <ImageUploader
            existing={listing?.photos || []}
            onChange={setPhotos}
            label="property photos"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 p-6 bg-slate-50 border-t border-slate-100">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? "Saving..." : listing ? "Update Listing" : "Create Listing"}
        </Button>
      </div>
    </form>
  );
}
